import { useSettings } from "components/Clubs/hooks";
import { VerticalSpaceTable } from "components/SpaceBooking/VerticalSpaceTable/VerticalSpaceTable";
import { TabBar, TabBarSection } from "components/TabBar";
import { WYSIWYGText } from "components/WYSIWYGText";
import { Button } from "components/styledComponents/Button";
import { useModal } from "hooks/useModal";
import Link from "next/link";
import { useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import styled from "styled-components";
import useSWR from "swr";
import { SpaceTable } from ".";
import { SpaceSubscriptionsOverview } from "./Subscription";
import { SpaceGroup } from "./types";
import { usePersistentState } from "hooks";

const WarningMessageContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const StyledSpaceBookingPage = styled.div`
    position: relative;
    padding: 1rem;

    @media (max-width: 650px) {
        padding: 0;
    }

    .space-group-description {
        padding: 1rem;
        border-radius: ${({ theme }) => theme.borderRadius};
        box-shadow: ${({ theme }) => theme.shadows.sm};
        border: 1px solid ${({ theme }) => theme.borderColor};

        margin-bottom: 2rem;
    }

    .space-group-table {
        width: 100%;
        height: calc(100vh - 65px);
    }
`;

const StyledTabBar = styled.div`
    margin-top: -0.5rem;

    > div {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    hr {
        margin-top: 0.5rem;
        margin-bottom: 1rem;
    }
`;

interface Props {
    clubSlug: string;
    ownClub?: boolean;
}

export const SpaceBookingPage = ({ clubSlug, ownClub = false }: Props) => {
    const [setModal] = useModal();
    const [{
        view: clubViewSettings,
        hide_subscriptions
    } = {
        view: { vertical: false },
        hide_subscriptions: true
    }, { isLoading: settingsIsLoading }] = useSettings("space-bookings", clubSlug);
    const [userViewSettings, setUserViewSettings] = usePersistentState(undefined, "space-booking-page");
    const viewSettings = !userViewSettings ? clubViewSettings : userViewSettings;

    const { data, error } = useSWR<{ data: SpaceGroup[] }>(`space/group/${clubSlug}`);
    const isLoading = (!data && !error) || settingsIsLoading;
    const [currentGroup, setCurrentGroup] = useState<SpaceGroup>(data?.data?.[0]);

    const handleOpenSubscriptionsList = () =>
        setModal({
            title: "Platzabos",
            content: <SpaceSubscriptionsOverview clubSlug={clubSlug} />,
            show: true
        });

    const sections: TabBarSection[] =
        data?.data.map((group) => ({
            key: String(group.id),
            title: group.name,
            content: String(group.id)
        })) || [];

    return (<>
        <StyledTabBar className="container">
            <div>
                <TabBar
                    urlKey="spaces_tab"
                    sections={sections}
                    getSectionIndex={(sectionIndex) => setCurrentGroup(data?.data[sectionIndex])}
                />
                <div>
                    <Button
                        className="mr-3 btn-light"
                        small
                        onClick={() => setUserViewSettings({ vertical: !(viewSettings?.vertical || false) })}
                    >
                        {viewSettings?.vertical ? "Horizontale Ansicht" : "Vertikale Ansicht"}
                    </Button>
                    {!hide_subscriptions &&
                        <Button className="mr-3" small onClick={() => handleOpenSubscriptionsList()}>
                            Platzabos
                        </Button>}
                    {ownClub && (
                        <Link href={`/spaces/${clubSlug}/admin`} className="btn btn-success btn-sm">
                            Backend
                        </Link>
                    )}
                </div>
            </div>
            <hr />
        </StyledTabBar>
        <StyledSpaceBookingPage>
            {currentGroup?.description && (
                <div className="container">
                    <WYSIWYGText className="space-group-description">{currentGroup?.description}</WYSIWYGText>
                </div>
            )}
            <DndProvider backend={HTML5Backend}>
                {data?.data.length > 0 ? (
                    viewSettings?.vertical ? (
                        <VerticalSpaceTable
                            isOwner={ownClub}
                            className="space-group-table"
                            spaceGroup={currentGroup}
                            viewSettings={viewSettings}
                        />
                    ) : (
                        <SpaceTable isOwner={ownClub} className="space-group-table" spaceGroup={currentGroup} />
                    )
                ) : (
                    <WarningMessageContainer>
                        <h5>Es wurden noch keine Platzgruppen erstellt! </h5>
                    </WarningMessageContainer>
                )}
            </DndProvider>
        </StyledSpaceBookingPage>
    </>);
};

export default SpaceBookingPage;
